import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, refreshUser } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && token) {
      pollPaymentStatus(sessionId);
    } else if (!sessionId) {
      setStatus('failed');
    }
  }, [searchParams, token]);

  const pollPaymentStatus = async (sessionId) => {
    if (attempts >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/subscriptions/checkout/status/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error('Failed to check status');

      const data = await res.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(data);
        // Refresh user data to get new subscription status
        if (refreshUser) {
          await refreshUser();
        }
      } else if (data.status === 'expired') {
        setStatus('failed');
      } else {
        // Keep polling
        setAttempts(prev => prev + 1);
        setTimeout(() => pollPaymentStatus(sessionId), 2000);
      }
    } catch (err) {
      console.error('Error checking payment:', err);
      setAttempts(prev => prev + 1);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId), 2000);
      } else {
        setStatus('failed');
      }
    }
  };

  const getPlanName = (tier) => {
    if (tier === 'tier1') return 'Essential';
    if (tier === 'tier2') return 'Premium';
    return tier;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F0E8] to-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full rounded-2xl p-8 text-center" data-testid="subscription-success-card">
        {status === 'checking' && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
            <h1 className="text-2xl font-nunito font-bold text-[#2D2D2D] mb-2">
              Processing Payment...
            </h1>
            <p className="text-[#57534E]">
              Please wait while we confirm your subscription.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-nunito font-bold text-[#2D2D2D] mb-2">
              Welcome to {getPlanName(paymentData?.tier)}!
            </h1>
            <p className="text-[#57534E] mb-6">
              Your subscription is now active. You have full access to all {getPlanName(paymentData?.tier)} features.
            </p>
            
            {paymentData && (
              <div className="bg-stone-50 rounded-xl p-4 mb-6 text-left">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-[#57534E]">Plan</span>
                  <span className="font-medium text-[#2D2D2D]">{getPlanName(paymentData.tier)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#57534E]">Amount Paid</span>
                  <span className="font-medium text-[#2D2D2D]">
                    ${paymentData.amount} {paymentData.currency?.toUpperCase()}
                  </span>
                </div>
              </div>
            )}

            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-primary hover:bg-primary/90 text-white"
            >
              Go to Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-6">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-nunito font-bold text-[#2D2D2D] mb-2">
              Payment Issue
            </h1>
            <p className="text-[#57534E] mb-6">
              We couldn't confirm your payment. If you believe this is an error, please contact support.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/pricing')}
                className="w-full bg-primary hover:bg-primary/90 text-white"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="w-full"
              >
                Go to Dashboard
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default SubscriptionSuccessPage;
